// Playtime from the website, the way the launcher reports it (launcher/src/playtime.rs): "Play in browser" sessions go
// to accounts' POST /v1/playtime, through a queue in localStorage written before anything is sent, so a closed tab or a
// failed call loses nothing and a resend is a duplicate on the service, never double time.
//
// The site can't see into a game's own tab, so a web session is the time the player spent away in it, from the Play in
// browser click until this page is visible again. Accounts keeps these (`web-` ids) out of the creator pool and counts
// them for standing: Citizen minutes, players per game, and the shipped-note guard (docs/build/plaza-status.md).
// Consent is the account's `consent.playtime`, checked by accounts (403 consent_required: nothing is kept).

import { api, ApiError } from "./api.ts";
import { currentMe } from "./session.ts";

/** accounts takes at most this many per call */
export const BATCH = 100;
/** beyond this the oldest entries go */
const MAX_QUEUED = 500;
/** accounts refuses longer web sessions (MAX_WEB_SESSION_SECONDS); longer stays are cut to this */
export const MAX_WEB_SESSION_SECONDS = 3 * 3600;
const KEY = "thrdm.playtime";

export interface PlaySession {
  id: string;
  gameId: string;
  startedAt: number; // unix s
  endedAt: number; // unix s
  activeSeconds: number;
}

interface Queued {
  user: string;
  session: PlaySession;
}

/** A browser session's id names its game and start (unix ms, base 36), as community's shipped-note guard reads it. */
export function webSessionId(gameId: string, startMs: number): string {
  return `web-${gameId.replace(/[^a-z0-9_-]/g, "").slice(0, 40)}-${Math.floor(startMs).toString(36)}`;
}

/** The session accounts takes, or null when there's nothing to record (not a catalogue game, no time). */
export function webSession(gameId: string, startMs: number, endMs: number, id = webSessionId(gameId, startMs)): PlaySession | null {
  if (!/^g_[a-z0-9]+(?:-[a-z0-9]+)*$/.test(gameId) || !(endMs > startMs)) return null;
  const startedAt = Math.floor(startMs / 1000);
  const endedAt = Math.min(Math.ceil(endMs / 1000), startedAt + MAX_WEB_SESSION_SECONDS);
  if (endedAt <= startedAt) return null;
  return { id, gameId, startedAt, endedAt, activeSeconds: endedAt - startedAt };
}

function load(): Queued[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((q) => q && typeof q.user === "string" && q.session && typeof q.session.id === "string") : [];
  } catch {
    return [];
  }
}

function save(q: Queued[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(q.slice(-MAX_QUEUED)));
  } catch {
    /* private mode or full storage: this session is sent once, without a retry */
  }
}

let flushing: Promise<void> | null = null;

/** Sends the signed-in player's queued sessions. Others' entries wait for them to sign in again. */
export function flushPlaytime(): Promise<void> {
  flushing ??= (async () => {
    try {
      const me = currentMe()?.id;
      if (!me) return;
      for (;;) {
        const mine = load().filter((q) => q.user === me).slice(0, BATCH);
        if (!mine.length) return;
        let done: Set<string>;
        try {
          await api("accounts", "/playtime", { method: "POST", body: { sessions: mine.map((q) => q.session) } });
          // recorded, duplicates and refusals alike: none of them gets better by sending again
          done = new Set(mine.map((q) => q.session.id));
        } catch (e) {
          // playtime is off in settings: the service keeps nothing, so neither do we
          if (e instanceof ApiError && e.status === 403 && e.code === "consent_required") done = new Set(mine.map((q) => q.session.id));
          else return; // offline, signed out, the service down: keep them for next time
        }
        save(load().filter((q) => !(q.user === me && done.has(q.session.id))));
      }
    } finally {
      flushing = null;
    }
  })();
  return flushing;
}

/** Queues a finished browser session for the signed-in player and sends it. */
export function reportWebSession(session: PlaySession | null): Promise<void> {
  const me = currentMe()?.id;
  if (!session || !me) return Promise.resolve();
  const q = load();
  if (!q.some((x) => x.user === me && x.session.id === session.id)) save([...q, { user: me, session }]);
  return flushPlaytime();
}
